import ContentHeader from "pages/Profile/components/ContentHeader";
import styles from "./ScheduleEditingPage.module.scss";
import Button, { SIZE } from "components/Button";
import { CircleClose, Loading } from "components/assets/Icons";
import { useEffect, useState } from "react";
import { ModalAddBreak, ModalAddWorkTimeForecasting } from "../Modals";
import Modal from "components/Modal";
import Header from "../AddPage/components/Header";
import { formatListOfBreaks } from "utils/formatListOfBreaks";
import { changeValueOfTime } from "utils/changeValueOfTime";
import { timeDifference } from "utils/timeDifference";
import { toggleDisabledDays } from "utils/toggleDisabledDays";
import { days } from "utils/constants";
import { formatCurrentBrake } from "utils/formatCurrentBrake";
import { groupDaysWithSameHours } from "utils/groupDaysWithSameHours";
import { getShiftRow } from "../AddPage/utils/getShiftRow";
import Message from "../Message";
import userProfile from "services/user.profile";

const ScheduleEditingPage = ({
  setCurrentScreen,
  currentScreen,
  breaksList,
  editingId,
  schedulesData,
  schedulesList,
  setShowMessage,
  setMessage,
}) => {
  const workingHoursObject = schedulesData[editingId].shifts;
  const groupedHoursArray = groupDaysWithSameHours(workingHoursObject);
  const editingTime = groupedHoursArray.map((day) => parseTimeString(day.time));

  function parseTimeString(timeString) {
    const [start, end] = timeString.split("-");
    const [startHours, startMinutes] = start.split(":");
    const [endHours, endMinutes] = end.split(":");

    return {
      start_hours: startHours,
      start_minutes: startMinutes,
      end_hours: endHours,
      end_minutes: endMinutes,
    };
  }

  const [daysList, setDaysList] = useState(
    groupedHoursArray || [getShiftRow()],
  );
  const [typeOfMessage, setTypeOfMessage] = useState("info");
  const [infoMessage, setInfoMessage] = useState("");
  const [showInfoMessage, setShowInfoMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalState, setModalState] = useState("");
  const [scheduleName, setScheduleName] = useState(
    schedulesData[editingId]?.name || "",);
  const [selectedBreak, setSelectedBreak] = useState(
    formatCurrentBrake(schedulesData[editingId].breakDuration) || "",);
  const [selectedTimezone, setSelectedTimezone] = useState({
    title: "PST",
    value: "US/Pacific",
  });
  const [selectedForecasting, setSelectedForecasting] = useState(
    schedulesData[editingId]?.work_time_forecasting || "",
  );
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState(
    groupedHoursArray ? groupedHoursArray.map(({ days }) => days) : [],);
  const [time, setTime] = useState(
    editingTime || [
      { start_hours: "", start_minutes: "", end_hours: "", end_minutes: "" },
    ],
  );
  const [duration, setDuration] = useState([]);
  const [formErrors, setFormErrors] = useState({
    scheduleName: false,
    breaks: false,
    timezone: false,
    forecasting: false,
    days: false,
    time: false,
  });
  const daysOptions = toggleDisabledDays(selectedDayOfWeek, days);

  const editedSchedule = {
    id: schedulesData[editingId].id,
    name: scheduleName,
    breaks: selectedBreak,
    timezone: selectedTimezone.value,
    forecasting: selectedForecasting,
    shift: { days: selectedDayOfWeek, time, duration },
  };

  useEffect(() => {
    const newDuration = time.map(
      ({ start_hours, start_minutes, end_hours, end_minutes }) =>
        timeDifference(start_hours, start_minutes, end_hours, end_minutes),
    );
    setDuration(newDuration);
  }, [
    setCurrentScreen,
    time,
    selectedBreak,
    selectedTimezone,
    selectedForecasting,
    selectedDayOfWeek,
    scheduleName,
  ]);

  const addDay = () => {
    setDaysList((prevDaysList) => [...prevDaysList, getShiftRow()]);
    setDuration((prevDuration) => [...prevDuration, ""]);
    setTime((prevTime) => [
      ...prevTime,
      {
        start_hours: "",
        start_minutes: "",
        end_hours: "",
        end_minutes: "",
      },
    ]);
  };

  const deleteDay = (id) => {
    setDaysList((prevDaysList) =>
      prevDaysList.filter((_, index) => index !== id),
    );
    setTime((prevTime) => prevTime.filter((_, index) => index !== id));
    setDuration((prevDuration) =>
      prevDuration.filter((_, index) => index !== id),
    );
    setSelectedDayOfWeek((prev) => prev.filter((_, index) => index !== id));
  };

  const handleChange = (e) => {
    const { value } = e.target;
    setScheduleName(value);
    setFormErrors({
      scheduleName: false,
      breaks: false,
      timezone: false,
      forecasting: false,
      days: false,
      time: false,
    });
    setLoading(false);
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    const scheduleNames = schedulesList.map((schedule) => schedule.name);
    const isNameMatched = scheduleNames.some(
      (name) => name.toLowerCase() === scheduleName.toLowerCase(),
    );

    const currentScheduleName = schedulesData[editingId]?.name;
    if (isNameMatched && currentScheduleName !== scheduleName.toLowerCase()) {
      setFormErrors({
        ...formErrors,
        form: `This name is already in use`,
        name: true,
      });
      return;
    }

    const newErrors = {};

    if (editedSchedule.name.trim() === "") {
      newErrors.name = true;
    }
    if (editedSchedule.breaks.trim() === "") {
      newErrors.breaks = true;
    }
    if (editedSchedule.timezone.trim() === "") {
      newErrors.timezone = true;
    }
    if (editedSchedule.forecasting.trim() === "") {
      newErrors.forecasting = true;
    }
    if (editedSchedule.shift.days.length === 0) {
      newErrors.days = true;
    }
    if (editedSchedule.shift.time.length === 0) {
      newErrors.time = true;
    }
    if (editedSchedule.shift.days.some((dayArray) => dayArray.length === 0)) {
      newErrors.days = true;
      setTypeOfMessage("error");
      setInfoMessage(`Check the fields, all are required`);
      setShowInfoMessage(true);
      setTimeout(() => setShowInfoMessage(false), 1900);
    }
    if (editedSchedule.shift.days.length < daysList.length) {
      newErrors.days = true;
      setTypeOfMessage("error");
      setInfoMessage(`Check the fields, all are required`);
      setShowInfoMessage(true);
      setTimeout(() => setShowInfoMessage(false), 1900);
    }

    const isValidTime = editedSchedule.shift.time.some((timeObj) => {
      return Object.values(timeObj).some((value) => value.trim() === "");
    });
        if (isValidTime) {
      newErrors.time = true;
      setTypeOfMessage("error");
      setInfoMessage(`Check the fields, all are required`);
      setShowInfoMessage(true);
      setTimeout(() => setShowInfoMessage(false), 1900);
    }
    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    const editingSchedule = async (editedSchedule) => {
      setLoading(true);
      try {
        const response = await userProfile.editSchedule(editedSchedule);
        setTimeout(() => setCurrentScreen(""), 500);
        if (response.status === 200) {
          setTypeOfMessage("success");
          setMessage(`Schedule "${editedSchedule.name}" updated successfully`);
          setTimeout(() => setShowMessage(true), 900);
          setTimeout(() => setShowMessage(false), 2900);
        }
      } catch (error) {
        if (error?.response?.status === 500) {
          setInfoMessage("Ooops, something went wrong. Check all fields");
          setShowInfoMessage(true);
          setTimeout(() => setShowInfoMessage(false), 2500);
        }
        console.error("error:>>", error);
      } finally {
        setLoading(false);
      }
    };

    editingSchedule(editedSchedule);
  };

  return (
    <div>
      <ContentHeader
        title={schedulesData[editingId].name}
        handleClickBack={() => setCurrentScreen("")}
        custom_wrapper={styles.contentheader}
      />
      <form
        className={styles.form}
        method="GET"
        action="submit"
        onSubmit={handleOnSubmit}
      >
        <div className={styles.wrapper}>
          <ul className={styles.scheduleList}>
            <li>
              <Header
                editingId={editingId}
                breaksList={formatListOfBreaks(breaksList)}
                setShowModal={setShowModal}
                setModalState={setModalState}
                schedulesData={schedulesData}
                currentScreen={currentScreen}
                setSelectedTimezone={setSelectedTimezone}
                selectedTimezone={selectedTimezone}
                setSelectedForecasting={setSelectedForecasting}
                selectedForecasting={selectedForecasting}
                setSelectedBreak={setSelectedBreak}
                selectedBreak={selectedBreak}
                scheduleName={scheduleName}
                handleChange={handleChange}
                setLoading={setLoading}
                formErrors={formErrors}
              />
            </li>

            {daysList.map((day, idRow) => (
              <li key={idRow} className={styles.row}>
                {getShiftRow(
                  idRow,
                  changeValueOfTime,
                  duration[idRow],
                  time[idRow],
                  setTime,
                  day,
                  setSelectedDayOfWeek,
                  selectedDayOfWeek[idRow],
                  daysOptions,
                  formErrors,
                )}
                <button
                  className={styles.button_circleClose}
                  type="button"
                  onClick={() => deleteDay(idRow)}
                >
                  <CircleClose />
                </button>
              </li>
            ))}
          </ul>
          <div>
            <button
              type="button"
              className={styles.add_button}
              onClick={() => addDay()}
            >
              + Add a Day
            </button>
          </div>
          <div>
            <Button
              type="submit"
              size={SIZE.MIDDLE}
              className={styles.save_button}
              disabled={loading}
            >
              {loading ? <Loading /> : "Save"}
            </Button>
          </div>
        </div>
      </form>
      {showInfoMessage && (
        <Message
          text={infoMessage}
          showMessage={showInfoMessage}
          typeOfMessage={typeOfMessage}
          customStyles={{ top: "35px" }}
        />
      )}
      {showModal && (
        <Modal
          setShowModal={setShowModal}
          className={styles.modal}
          content={
            modalState === "addBrake" ? (
              <ModalAddBreak
                setShowModal={setShowModal}
                typeOfMessage={typeOfMessage}
              />
            ) : modalState === "addWorkTimeForecasting" ? (
              <ModalAddWorkTimeForecasting setShowModal={setShowModal} />
            ) : (
              <></>
            )
          }
        />
      )}
    </div>
  );
};
export default ScheduleEditingPage;
